import { Injectable } from '@angular/core';

import { AppConfig } from '®shared/config/app-config';

import { User } from './user';

@Injectable()
export class UserScopesService {
  constructor(private appConfig: AppConfig) {}

  /**
   * Check if a given user has a given scope.
   *
   * @param  {User}    user  The user whose scopes are being checked
   * @param  {string}  scope The scope for which we are checking
   * @return {boolean}       `true` when the user has the scope; `false` otherwise
   */
  hasScope(user: User, scope: string): boolean {
    if (!scope || !scope.trim()) {
      return false;
    }
    return ((user && user.scopes) || []).indexOf(scope) > -1;
  }

  /**
   * Check if a user has a given scope or a higher privilege.
   *
   * @param  {User}    user  The user whose scopes are being checked
   * @param  {string}  scope The scope for which we are checking
   * @return {boolean}       `true` when the user has the scope or higher; `false` otherwise
   */
  hasScopeOrHigherPrivileges(user: User, scope: string): boolean {
    if (scope === this.appConfig.scopeSu()) {
      return this.hasScope(user, this.appConfig.scopeSu());
    }

    const scopeOrHigher = [
      scope,
      this.appConfig.scopeSuAtTenant(),
      this.appConfig.scopeSu(),
    ];

    return scopeOrHigher.some(someScope => this.hasScope(user, someScope));
  }

  /**
   * Calculate if a given user has any one of a list of given scopes (or higher-privileges than any one).
   *
   * @param  {User}     user      The user whose scopes are being checked
   * @param  {string[]} ...scopes The list of scopes for which we are checking
   * @return {boolean}            `true` when the user has at least one of the given scopes; `false` otherwise.
   */
  hasSomeScopeOrHigherPrivileges(user: User, ...scopes: string[]): boolean {
    if (!scopes || scopes.length === 0) {
      return true;
    }

    return scopes.some(scope => this.hasScopeOrHigherPrivileges(user, scope));
  }

  isAuthorizedAsMemberAtTenant(user: User): boolean {
    return this.hasSomeScopeOrHigherPrivileges(
      user,
      this.appConfig.scopeMemberAtTenant(),
    );
  }

  /**
   * Get required scopes for user to be considered authenticated.
   *
   * @param  {boolean}  isTalisLogin `true` if the user is a Talis user; `false` otherwise
   * @return {string[]}              an array of required scopes to authorize the user
   */
  requiredScopesToAuthorize(isTalisLogin: boolean): string[] {
    return isTalisLogin
      ? [this.appConfig.scopeSu()]
      : this.appConfig.scopesForLoggedInUsers();
  }
}
